<template>
  <div class="j-location-wrap">
    <el-popover
        placement="bottom-start"
        :width="$attrs.width || '500'"
        popper-class="j-location-popper"
        trigger="click"
        @show="showEvent"
        v-model="showMap">
      <span class="j-location-text" v-if="type === 'text'" slot="reference">
        <i v-if="latitude && longitude" class="el-icon-location j-location-icon selected j-location-text-icon" />
        <i v-else class="j-location-icon el-icon-location-outline j-location-text-icon" />
        <span>{{address}}</span>
      </span>
      <el-input v-else slot="reference" :placeholder="$attrs.placeholder || '点击定位'" v-model="address" readonly>
        <i v-if="latitude && longitude" slot="prefix" class="el-input__icon el-icon-location j-location-icon selected" />
        <i v-else slot="prefix" class="el-input__icon j-location-icon el-icon-location-outline" />
      </el-input>
      <JsxGisMap v-loading="loading" element-loading-background="rgba(255, 255, 255, 0.3)" ref="map" :env="gis" class="j-location-map" @click="mapClickEvent">
        <el-alert v-show="showTip" :title="tipText" type="warning" center show-icon />
        <el-radio-group v-model="mapType" class="j-location-radio-button" size="mini" @change="mapTypeChange">
          <el-radio-button label="电子地图" />
          <el-radio-button label="影像地图" />
        </el-radio-group>
      </JsxGisMap>
      <div v-if="!readOnly && type === 'select'" class="j-location-popper-footer">
        <el-button size="mini" @click="resetEvent">重置</el-button>
        <el-button size="mini" type="primary" @click="saveEvent">保存</el-button>
      </div>
    </el-popover>
  </div>
</template>

<script>
import Vue from "vue";
import { gis } from "@/config";
import JsxGisMap from "@/components/JComponents/JGisMap/jsx/JsxGisMap";
import layerMixins from "@/components/JComponents/JGisMap/mixins/layerMixins";
export default {
  name: "JLocation",
  components: {
    JsxGisMap: Vue.component('JsxGisMap', { mixins: [JsxGisMap, layerMixins] })
  },
  props: {
    type: { // select,text 文本模式默认只读不可修改
      type: String,
      default: 'select'
    },
    latitude: { // 经度 y
      type: [String, Number],
      default: ''
    },
    longitude: { // 纬度 x
      type: [String, Number],
      default: ''
    },
    address: { // 地址文本
      type: String,
      default: ''
    },
    gridCode: { // 地址文本
      type: [String, Number],
      default: ''
    },
    addressRule: { // 地址组成字段按顺序(具体字段名需要和后端接口返回值保持一致)
      type: Array,
      default: () => ['county', 'town', 'village', 'name']
    },
    scope: { // 行政区划可选范围 传可选的gridCode, 仅能选范围下的区域
      type: Array,
      default: null
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      gis,
      mapType: '电子地图',
      loading: false,
      tipText: '',
      showTip: false,
      showMap: false,
      tempPoint: {}
    }
  },
  methods: {
    showEvent () {
      if (this.latitude && this.longitude) this.drawPoint(this.longitude, this.latitude, this.address)
    },
    mapLoadEvent () {
      this.mapTypeChange(this.mapType)
    },
    drawPoint (longitude, latitude, address) {
      this.$refs.map && this.$refs.map.GisMapClearPoint()
      this.$refs.map.GisMapDrawPoint({ longitude, latitude, address }, true)
    },
    mapClickEvent (event) {
      if (this.readOnly || this.type === 'text') return
      this.$refs.map && this.$refs.map.GisMapClearPoint()
      const { x, y } = event.mapPoint
      this.queryGridByXy(x, y)
    },
    async queryGridByXy (x,y) {
      if (this.loading) return
      this.loading = true
      const { data } = await this.$http.get(`/api/grids/gridInfo/mis`, { params: { x,y } })
          .catch(err => { this.warnEvent('当前区域超出行政区划范围'); console.error(err) })
          .finally(() => { this.loading = false })
      if (data.status !== 'yes') return this.$message.error(data.msg || data.message || '网格信息获取异常')
      if (!data.data) return this.warnEvent('当前区域超出行政区划范围')
      const gridInfo = data.data
      if (gridInfo.gridCode) {
        if (!this.checkScope(gridInfo.gridCode)) return this.warnEvent('当前区域已被限制，无法选择')
        const address = this.addressRule.map(rule => (gridInfo[rule] || rule)).join('')
        this.tempPoint = {
          address: address,
          gridCode: gridInfo.gridCode,
          longitude: x,
          latitude: y,
        }
        this.drawPoint(x, y, address)
      } else {
        this.warnEvent('当前区域无网格信息')
      }
    },
    checkScope (gridCode) {
      let isLimit = false
      if (!this.scope || this.scope.length < 1) return true // 范围参数为空或空数组代表全局范围可选
      this.scope.some(scope => {
        if (!scope) console.warn(`JLocation区划范围参数[scope]包含异常值:${scope}`)
        if (scope && String(gridCode).includes(scope)) return (isLimit = true)
      })
      return isLimit
    },
    mapTypeChange (type) {
      if (type === '电子地图') this.$refs.map && this.$refs.map.GisMapShowElectronMap()
      else this.$refs.map && this.$refs.map.GisMapShowSimulationMap()
    },
    warnEvent (tip, duration = 2000) {
      this.tempPoint = {}
      this.tipText = tip
      this.showTip = true
      setTimeout(() => { this.showTip = false }, duration)
    },
    resetEvent () {
      this.tempPoint = {}
      this.$refs.map && this.$refs.map.GisMapClearPoint()
    },
    saveEvent () {
      this.setData(this.tempPoint)
      this.showMap = false
      this.tempPoint = {}
      this.$refs.map && this.$refs.map.GisMapClearPoint()
    },
    setData (point) {
      this.$emit('update:address', point.address || '')
      this.$emit('update:gridCode', point.gridCode || '')
      this.$emit('update:longitude', point.longitude || '')
      this.$emit('update:latitude', point.latitude || '')
    }
  }
}
</script>

<style lang="scss">
  .j-location-wrap {
    .el-input__inner {
      cursor: pointer;
    }
    .j-location-icon {
      font-size: 14px;
      &.selected {
        color: $themeColor;
      }
    }
    .j-location-text {
      .j-location-text-icon {
        margin-right: 4px;
        cursor: pointer;
      }
    }
  }
  .j-location-popper {
    position: relative;
    .el-alert {
      position: absolute;
      bottom: 0;
      z-index: 1;
      border-radius: 0;
      box-sizing: border-box;
    }
    .j-location-map {
      height: 400px;
    }
    .j-location-popper-footer {
      text-align: right;
      margin: 0;
      padding-top: 10px;
    }
    .j-location-radio-button {
      position: absolute;
      z-index: 2;
      left: 10px;
      top: 10px;
    }
  }
</style>
